import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import i18next from "i18next";

import {
  acceptAllConsent,
  rejectAllConsent,
  setConsent,
  getConsentContext,
  OPEN_COOKIE_SETTINGS_EVENT,
} from "../../Utils/gtm";
import { refreshAnalyticsConsent } from "../../Utils/segment";

/**
 * CookieConsent — granular consent banner (GDPR-style).
 *
 * Two views:
 *   1. Compact bar: Reject all · Customize · Accept all
 *   2. Preferences panel: per-category toggles the user can save individually.
 *
 * Categories (mapped to Google Consent Mode v2 keys):
 *   - Necessary       → always on, not stored as a consent key (informational)
 *   - Analytics       → analytics_storage   (gates the Segment SDK)
 *   - Advertising     → ad_storage, ad_user_data, ad_personalization
 *
 * The decision is persisted by setConsent() in gtm.js (localStorage.consentMode
 * + the shared _tkg_consent cookie). Whenever analytics_storage ends up granted
 * we call refreshAnalyticsConsent() so the Segment SDK loads immediately in the
 * same session. If analytics is not granted, the SDK never loads — no anonymous
 * MTU is created for undecided, rejecting, or advertising-only visitors.
 */

// Map each user-facing category to the Consent Mode keys it controls.
const CATEGORY_KEYS = {
  analytics: ["analytics_storage"],
  advertising: ["ad_storage", "ad_user_data", "ad_personalization"],
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Optional categories default to OFF (opt-in). Pre-fill from an existing
  // decision when the panel is re-opened later.
  const [prefs, setPrefs] = useState({ analytics: false, advertising: false });

  // Sync the toggle state from the stored decision (if any).
  const syncPrefsFromStorage = () => {
    const existing = getConsentContext();
    setPrefs({
      analytics: !!existing && existing.analytics_storage === "granted",
      advertising: !!existing && existing.ad_storage === "granted",
    });
    return existing;
  };

  useEffect(() => {
    // First-run: show the banner only if no decision has been made yet.
    const existing = syncPrefsFromStorage();
    if (!existing) setVisible(true);

    // Allow re-opening the preferences from anywhere (e.g. footer link).
    const reopen = () => {
      syncPrefsFromStorage();
      setShowPreferences(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  // Build a full per-key decision map from the two category toggles.
  const buildDecision = (categoryState) => {
    const decision = {};
    Object.entries(CATEGORY_KEYS).forEach(([category, keys]) => {
      keys.forEach((key) => {
        decision[key] = categoryState[category] ? "granted" : "denied";
      });
    });
    return decision;
  };

  const applyAndClose = (snapshot) => {
    // Load Segment only if analytics ended up granted.
    if (snapshot && snapshot.analytics_storage === "granted") {
      refreshAnalyticsConsent();
    }
    setVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    const snapshot = acceptAllConsent();
    applyAndClose(snapshot);
  };

  const handleRejectAll = () => {
    const snapshot = rejectAllConsent();
    applyAndClose(snapshot);
  };

  const handleSavePreferences = () => {
    const snapshot = setConsent(buildDecision(prefs));
    applyAndClose(snapshot);
  };

  const toggle = (category) =>
    setPrefs((p) => ({ ...p, [category]: !p[category] }));

  if (!visible) return null;

  const containerStyle = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1080,
    background: "#ffffff",
    borderTop: "1px solid #e5e5e5",
    boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
    padding: "16px",
  };

  const innerStyle = {
    maxWidth: 1140,
    margin: "0 auto",
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={i18next.t("Cookie consent")}
      style={containerStyle}
    >
      <div style={innerStyle}>
        {!showPreferences ? (
          // ── Compact bar ────────────────────────────────────────────────
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "12px",
              justifyContent: "space-between",
            }}
          >
            <p style={{ margin: 0, flex: "1 1 320px", fontSize: 14, color: "#495057" }}>
              {i18next.t(
                "We use cookies to analyze traffic and improve your experience. Analytics only run if you accept."
              )}
            </p>
            <div style={{ display: "flex", gap: "8px", flex: "0 0 auto", flexWrap: "wrap" }}>
              <Button color="light" onClick={handleRejectAll}>
                {i18next.t("Reject")}
              </Button>
              <Button color="light" outline onClick={() => setShowPreferences(true)}>
                {i18next.t("Customize")}
              </Button>
              <Button color="primary" onClick={handleAcceptAll}>
                {i18next.t("Accept")}
              </Button>
            </div>
          </div>
        ) : (
          // ── Preferences panel ──────────────────────────────────────────
          <div>
            <h5 style={{ marginTop: 0, marginBottom: 12, color: "#343a40" }}>
              {i18next.t("Cookie preferences")}
            </h5>

            <ConsentRow
              title={i18next.t("Strictly necessary")}
              description={i18next.t(
                "Required for the site to function. Always active."
              )}
              checked
              disabled
            />
            <ConsentRow
              title={i18next.t("Analytics")}
              description={i18next.t(
                "Helps us understand how the site is used. Enables product analytics."
              )}
              checked={prefs.analytics}
              onChange={() => toggle("analytics")}
            />
            <ConsentRow
              title={i18next.t("Advertising")}
              description={i18next.t(
                "Used to measure ad performance and personalize marketing."
              )}
              checked={prefs.advertising}
              onChange={() => toggle("advertising")}
            />

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                marginTop: 16,
                flexWrap: "wrap",
              }}
            >
              <Button color="light" onClick={handleRejectAll}>
                {i18next.t("Reject")}
              </Button>
              <Button color="primary" outline onClick={handleSavePreferences}>
                {i18next.t("Save preferences")}
              </Button>
              <Button color="primary" onClick={handleAcceptAll}>
                {i18next.t("Accept")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ConsentRow — a single category row with a native checkbox toggle.
 * Uses a plain checkbox so it works without extra UI deps and stays accessible.
 */
const ConsentRow = ({ title, description, checked, disabled, onChange }) => (
  <label
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      padding: "10px 0",
      borderTop: "1px solid #f0f0f0",
      cursor: disabled ? "default" : "pointer",
      margin: 0,
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      style={{ marginTop: 3, flex: "0 0 auto" }}
      aria-label={title}
    />
    <span style={{ flex: "1 1 auto" }}>
      <span style={{ display: "block", fontWeight: 600, fontSize: 14, color: "#343a40" }}>
        {title}
      </span>
      <span style={{ display: "block", fontSize: 13, color: "#6c757d" }}>
        {description}
      </span>
    </span>
  </label>
);

export default CookieConsent;
